import { useMutation, useQueryClient } from '@tanstack/react-query';
import { queryKeys } from '../../config/queryClient';

/**
 * Custom hook for optimistic updates when leads are closed or converted
 * Handles React Query cache updates for remove/move operations
 */
export const useLeadActionsMutation = (leadStages) => {
  const queryClient = useQueryClient();

  /**
   * Remove a lead from React Query cache (for converted leads)
   */
  const removeLeadMutation = useMutation({
    mutationFn: async ({ leadId, columnId, filters = {} }) => {
      // This is an optimistic-only mutation, no API call needed
      // The actual API call was already done by convertToCase
      return { leadId, columnId };
    },

    onMutate: async ({ leadId, columnId, filters = {} }) => {
      // Cancel outgoing queries to prevent race conditions
      await queryClient.cancelQueries([...queryKeys.leads.column(columnId), filters]);

      // Store previous data for rollback
      const previousData = queryClient.getQueryData([...queryKeys.leads.column(columnId), filters]);

      // Optimistically remove the lead from the cache
      queryClient.setQueryData([...queryKeys.leads.column(columnId), filters], (old) => {
        if (!old?.pages) return old;

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((item) => item.sk !== leadId),
            totalCount: Math.max(0, (page.totalCount || 0) - 1),
          })),
        };
      });

      return { previousData, columnId, filters };
    },

    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousData && context?.columnId && context?.filters) {
        queryClient.setQueryData([...queryKeys.leads.column(context.columnId), context.filters], context.previousData);
      }
    },

    onSettled: (data, error, variables, context) => {
      // Invalidate to ensure consistency
      if (context?.columnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.leads.column(context.columnId), context.filters],
        });
      }
    },
  });

  /**
   * Move a lead to a different stage (for closed leads)
   */
  const moveLeadToStageMutation = useMutation({
    mutationFn: async ({ leadId, sourceColumnId, targetColumnId, filters = {} }) => {
      // This is an optimistic-only mutation, no API call needed
      // The actual API call was already done by closeLead
      return { leadId, sourceColumnId, targetColumnId };
    },

    onMutate: async ({ leadId, sourceColumnId, targetColumnId, filters = {} }) => {
      // Cancel outgoing queries for both columns
      await queryClient.cancelQueries([...queryKeys.leads.column(sourceColumnId), filters]);
      await queryClient.cancelQueries([...queryKeys.leads.column(targetColumnId), filters]);

      // Store previous data for rollback
      const previousSourceData = queryClient.getQueryData([...queryKeys.leads.column(sourceColumnId), filters]);
      const previousTargetData = queryClient.getQueryData([...queryKeys.leads.column(targetColumnId), filters]);

      // Get the lead item from source column
      let leadItem = null;
      const sourceData = queryClient.getQueryData([...queryKeys.leads.column(sourceColumnId), filters]);
      if (sourceData?.pages) {
        for (const page of sourceData.pages) {
          const found = page.items.find((item) => item.sk === leadId);
          if (found) {
            leadItem = { ...found, lead_stage: targetColumnId, is_open: false };
            break;
          }
        }
      }

      // Remove from source column
      queryClient.setQueryData([...queryKeys.leads.column(sourceColumnId), filters], (old) => {
        if (!old?.pages) return old;

        return {
          ...old,
          pages: old.pages.map((page) => ({
            ...page,
            items: page.items.filter((item) => item.sk !== leadId),
            totalCount: Math.max(0, (page.totalCount || 0) - 1),
          })),
        };
      });

      // Add to target column (if we found the lead item)
      if (leadItem) {
        queryClient.setQueryData([...queryKeys.leads.column(targetColumnId), filters], (old) => {
          if (!old?.pages) {
            // Initialize if no data exists
            return {
              pages: [
                {
                  items: [leadItem],
                  totalCount: 1,
                  hasMore: false,
                  page: 1,
                },
              ],
              pageParams: [1],
            };
          }

          // Add to first page
          return {
            ...old,
            pages: old.pages.map((page, index) => {
              if (index === 0) {
                return {
                  ...page,
                  items: [leadItem, ...page.items],
                  totalCount: (page.totalCount || 0) + 1,
                };
              }
              return page;
            }),
          };
        });
      }

      return { previousSourceData, previousTargetData, sourceColumnId, targetColumnId, filters };
    },

    onError: (error, variables, context) => {
      // Rollback on error
      if (context?.previousSourceData && context?.sourceColumnId && context?.filters) {
        queryClient.setQueryData([...queryKeys.leads.column(context.sourceColumnId), context.filters], context.previousSourceData);
      }
      if (context?.previousTargetData && context?.targetColumnId && context?.filters) {
        queryClient.setQueryData([...queryKeys.leads.column(context.targetColumnId), context.filters], context.previousTargetData);
      }
    },

    onSettled: (data, error, variables, context) => {
      // Invalidate both columns to ensure consistency
      if (context?.sourceColumnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.leads.column(context.sourceColumnId), context.filters],
        });
      }
      if (context?.targetColumnId && context?.filters) {
        queryClient.invalidateQueries({
          queryKey: [...queryKeys.leads.column(context.targetColumnId), context.filters],
        });
      }
    },
  });

  /**
   * Helper function to find the closed stage
   */
  const findClosedStage = () => {
    return leadStages?.find((stage) => stage.lead_stage_name === 'Closed');
  };

  return {
    removeLeadMutation,
    moveLeadToStageMutation,
    findClosedStage,
  };
};
